<script lang="ts" setup>
import { Globe2, Instagram, Linkedin, Send, Smartphone } from 'lucide-vue-next';
import { usePwaInstall } from '@/presentation/composables';
import { notify } from '@/presentation';

const androidDownloadUrl = import.meta.env.VITE_ANDROID_APP_URL || '';
const currentYear = new Date().getFullYear();
const { canPromptInstall, fallbackInstallMessage, isInstalled, requestInstall } = usePwaInstall();
const newtyLogoSrc = '/images/newty-logo.png';

const footerColumns = [
	{
		title: 'راهنما',
		links: [
			{ title: 'چگونه نوبت بگیریم؟', href: '#' },
			{ title: 'راهنمای پرداخت', href: '#' },
			{ title: 'حریم خصوصی', href: '#' },
			{ title: 'شرایط استفاده', to: { name: 'TermsAndCondition' } },
			{ title: 'قوانین بیعانه و کنسلی', to: { name: 'DepositCancellationPolicy' } },
		],
	},
	{
		title: 'نیوتی',
		links: [
			// { title: 'درباره ما', href: '#' },
			{ title: 'تماس با ما', to: { name: 'ContactUs' } },
			// { title: 'سوالات متداول', href: '#' },
			{ title: 'قوانین و مقررات', to: { name: 'TermsAndCondition' } },
		],
	},
	{
		title: 'دسته‌بندی‌ها',
		links: [
			{ title: 'سالن‌های زیبایی', to: { name: 'Explore' } },
			{ title: 'کلینیک‌های زیبایی', to: { name: 'Explore' } },
			{ title: 'تتو آرتیست ها', to: { name: 'Explore' } },
			{ title: 'آموزشگاه ها', to: { name: 'Explore' } },
		],
	},
];

const socialLinks = [
	{ title: 'اینستاگرام', href: 'https://www.instagram.com/newty.ir', icon: Instagram },
	{ title: 'تلگرام', href: 'https://t.me/newty_support', icon: Send },
	{ title: 'لینکدین', href: '#', icon: Linkedin },
];

const installWebApp = async () => {
	const outcome = await requestInstall();
	const message = outcome === 'accepted' ? 'درخواست نصب وب اپلیکیشن ثبت شد.' : outcome === 'dismissed' ? 'نصب وب اپلیکیشن لغو شد.' : fallbackInstallMessage();

	notify({
		message,
		duration: 5000,
		showCloseButton: true,
	});
};
</script>

<template>
	<footer
		class="mx-auto mt-10 w-full max-w-[1400px] px-4 pb-[calc(var(--app-bottom-nav-height,5rem)+2.5rem)] sm:px-6 md:pb-12 lg:px-8"
		aria-labelledby="site-footer-title"
	>
		<section class="rounded-3xl border border-[#E8EEF7] bg-white px-6 py-8 shadow-[0_12px_34px_rgba(42,60,85,0.06)] md:px-10">
			<div class="grid gap-8 lg:grid-cols-[1.1fr_2fr_1.2fr]">
				<div>
					<img :src="newtyLogoSrc" alt="Newty" class="h-12 w-auto object-contain" />
					<p id="site-footer-title" class="mt-4 max-w-xs text-sm font-medium leading-8 text-[#64748B]">
						نیوتی، پلتفرم آنلاین رزرو خدمات زیبایی، با هدف سهولت در دریافت خدمات و بهترین تجربه کاربری.
					</p>
					<div class="mt-5 flex gap-2">
						<a
							v-for="social in socialLinks"
							:key="social.title"
							:href="social.href"
							target="_blank"
							rel="noopener noreferrer"
							class="flex h-10 w-10 items-center justify-center rounded-full bg-[#F4F6FB] text-[#475569] transition hover:bg-[#4848FE] hover:text-white"
							:aria-label="social.title"
						>
							<component :is="social.icon" class="h-5 w-5" />
						</a>
					</div>
				</div>

				<div class="grid gap-6 sm:grid-cols-3">
					<div v-for="column in footerColumns" :key="column.title">
						<h3 class="text-sm font-black text-[#121826]">{{ column.title }}</h3>
						<ul class="mt-4 space-y-3">
							<li v-for="link in column.links" :key="link.title">
								<component
									:is="link.to ? 'RouterLink' : 'a'"
									:to="link.to"
									:href="link.href"
									class="text-sm font-medium text-[#64748B] transition hover:text-[#4848FE]"
								>
									{{ link.title }}
								</component>
							</li>
						</ul>
					</div>
				</div>

				<div>
					<h3 class="text-sm font-black text-[#121826]">دانلود اپلیکیشن نیوتی</h3>
					<p class="mt-3 text-xs font-medium leading-6 text-[#64748B]">اپلیکیشن ما را از لینک‌های زیر دانلود کنید.</p>
					<div class="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
						<button
							type="button"
							class="flex min-h-[3.5rem] items-center justify-center gap-3 rounded-lg border border-[#E8EEF7] bg-white px-4 py-3 text-xs font-black text-[#121826] transition hover:border-[#4848FE]/30 hover:text-[#4848FE]"
							:class="{ 'cursor-not-allowed opacity-60': isInstalled }"
							:disabled="isInstalled"
							@click="installWebApp"
						>
							<Globe2 class="h-5 w-5" />
							<span>{{ isInstalled ? 'نسخه PWA نصب شده' : canPromptInstall ? 'نصب سریع PWA' : 'نصب نسخه وب (PWA)' }}</span>
						</button>
						<a
							:href="androidDownloadUrl || undefined"
							class="flex min-h-[3.5rem] items-center justify-center gap-3 rounded-lg border border-[#E8EEF7] bg-white px-4 py-3 text-xs font-black text-[#121826] transition hover:border-[#4848FE]/30 hover:text-[#4848FE]"
							:class="{ 'pointer-events-none opacity-60': !androidDownloadUrl }"
							:aria-disabled="!androidDownloadUrl"
						>
							<Smartphone class="h-5 w-5" />
							<span>دانلود اندروید</span>
						</a>
					</div>
				</div>
			</div>

			<div class="mt-8 border-t border-[#E8EEF7] pt-5 text-center text-xs font-medium text-[#64748B]">© {{ currentYear }} نیوتی، تمامی حقوق محفوظ است.</div>
		</section>
	</footer>
</template>
